import { useMemo } from 'react';
import { parseSources, extractCitedDocuments, type CitationInfo, type Document } from '@/utils/citation-utils';

/**
 * Custom hook for parsing citations from markdown content
 * Consolidates duplicate citation parsing logic across components
 *
 * @param content - The markdown content containing citations
 * @param documents - Optional list of documents to validate citations against
 * @returns Object with sources map and cited documents map
 */
export function useCitationParsing(content: string | null, documents?: Document[]) {
  // Parse citations from the Citations section (or auto-generate from [cite:GUID] if missing)
  const sources = useMemo(() => {
    if (!content) {
      return new Map<string, { documentGuid: string }>();
    }
    return parseSources(content, documents);
  }, [content, documents]);

  // Extract cited documents grouped by document GUID
  const citedDocuments = useMemo(() => {
    if (!content) return new Map<string, CitationInfo[]>();
    return extractCitedDocuments(content, sources, documents);
  }, [content, sources, documents]);

  return { sources, citedDocuments };
}
